import React from 'react'
import { Checkbox } from '@mui/material'
import { useDispatch } from 'react-redux'
import { updateOrderItems } from 'features/tab/tab'
import { IOrderDetail, ITabDetail, ITabItem } from 'interfaces/tab'

interface TableDetailsProps {
  tabDetailData: ITabDetail
  items: ITabItem[]
  selectedSum: number
  selectedDiscounts: number
  calculateSelectedTax: () => number
  calculateSelectedServiceCharge: () => number
  calculateTotalAfterTax: () => string
}

const TableDetails = ({
  tabDetailData,
  items,
  selectedSum,
  selectedDiscounts,
  calculateSelectedTax,
  calculateSelectedServiceCharge,
  calculateTotalAfterTax,
}: TableDetailsProps) => {
  const dispatch = useDispatch()

  const isOrderPaid = (order: IOrderDetail) => {
    // Check if all items in the order are paid
    return order.items.every((item) => item.isPaid)
  }

  const hasUnpaidItems = (order: IOrderDetail) => {
    // Check if the order has any unpaid items
    return order.items.some((item) => !item.isPaid)
  }

  const hasPartialPayments = (order: IOrderDetail) => {
    // Check if the order has both paid and unpaid items (partial payment)
    return hasUnpaidItems(order) && order.items.some((item) => item.isPaid)
  }

  return (
    <div className="card mb-50">
      <div className="table-details-header">
        <p>TAB {tabDetailData?.publicTabId}</p>
        <span>
          {(() => {
            const totalQuantity =
              items?.reduce((sum, item) => sum + item.quantity, 0) || 0
            return totalQuantity.toFixed(2)
          })()}
          {(() => {
            const totalQuantity =
              items?.reduce((sum, item) => sum + item.quantity, 0) || 0
            return totalQuantity !== 1 ? ' Items' : ' Item'
          })()}
        </span>
      </div>
      {items.length > 0 && (
        <div className="table-details-body">
          <div className="table-details-collapse">
            {tabDetailData?.orders?.map(
              (order) =>
                order?.items?.length > 0 && (
                  <React.Fragment key={order.orderId}>
                    <label htmlFor={`${order.orderId} + 'lable'`}>
                      <div
                        className={`check-list ${
                          // eslint-disable-next-line no-nested-ternary
                          isOrderPaid(order)
                            ? 'paid'
                            : hasPartialPayments(order)
                            ? 'partial'
                            : ''
                        }`}
                      >
                        {isOrderPaid(order) ? (
                          <div className="check-grey-small" />
                        ) : (
                          <Checkbox
                            id={`${order.orderId} + 'lable'`}
                            icon={<div className="custom-checkbox" />}
                            checkedIcon={
                              <div className="custom-checkbox checked" />
                            }
                            checked={order?.isSelected && hasUnpaidItems(order)}
                            onChange={(e) => {
                              dispatch(
                                updateOrderItems({
                                  orderId: order.orderId,
                                  isChecked: e.target.checked,
                                })
                              )
                            }}
                            disabled={isOrderPaid(order)}
                          />
                        )}
                        <p>Check {order?.omnivoreTicketNo}</p>
                        {isOrderPaid(order) && (
                          <p className="pay-status pay-page">paid</p>
                        )}
                        {hasPartialPayments(order) && (
                          <p className="pay-status pay-page">partial</p>
                        )}
                        <span>
                          {(() => {
                            const totalQuantity = order.items
                              .filter((item) => !item.isPaid)
                              .reduce((sum, item) => sum + item.quantity, 0)
                            return totalQuantity.toFixed(2)
                          })()}
                          {(() => {
                            const totalQuantity = order.items
                              .filter((item) => !item.isPaid)
                              .reduce((sum, item) => sum + item.quantity, 0)
                            return totalQuantity !== 1 ? ' Items' : ' Item'
                          })()}
                          <strong>
                            $
                            {(() => {
                              const unpaidTotal = order.items
                                .filter((item) => !item.isPaid)
                                .reduce((sum, item) => sum + item.price, 0)
                              return (
                                isOrderPaid(order)
                                  ? order.subTotal
                                  : unpaidTotal
                              ).toFixed(2)
                            })()}
                          </strong>
                        </span>
                      </div>
                    </label>
                    <ul className="check-ul">
                      {order?.items?.map((item) => (
                        <li
                          key={item.posItemId}
                          className={item.isPaid ? 'paid-item' : ''}
                        >
                          <span
                            style={{
                              textDecoration:
                                item.isPaid && hasPartialPayments(order)
                                  ? 'line-through'
                                  : 'none',
                              opacity:
                                item.isPaid && hasPartialPayments(order)
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            {' '}
                            {item.name}{' '}
                            {Math.abs(item.quantity - 1) > 0.001
                              ? `× ${item.quantity.toFixed(2)}`
                              : ''}
                          </span>
                          <span
                            style={{
                              textDecoration:
                                item.isPaid && hasPartialPayments(order)
                                  ? 'line-through'
                                  : 'none',
                              opacity:
                                item.isPaid && hasPartialPayments(order)
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            ${item.price.toFixed(2)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                )
            )}
          </div>
          <ul>
            <li className="separator" />
            {/* Show POS discounts if any selected order has discounts */}
            {tabDetailData?.orders?.some(
              (order) =>
                order.isSelected && order.isOpen && order.totalDiscounts > 0
            ) && (
              <li className="remaining-amount">
                <span style={{ color: '#737373' }}>POS Discounts</span>
                <p style={{ color: '#737373' }}>
                  -${selectedDiscounts.toFixed(2)}
                </p>
              </li>
            )}
            <li className="remaining-amount">
              <span style={{ color: '#737373' }}>Sub Total</span>
              <p style={{ color: '#737373' }}>${selectedSum?.toFixed(2)}</p>
            </li>
            <li className="remaining-amount">
              <span style={{ color: '#737373' }}>Tax</span>
              <p style={{ color: '#737373' }}>
                ${calculateSelectedTax().toFixed(2)}
              </p>
            </li>
            {calculateSelectedServiceCharge() > 0 && (
              <li className="remaining-amount">
                <span style={{ color: '#737373' }}>Auto Gratuity</span>
                <p style={{ color: '#737373' }}>
                  ${calculateSelectedServiceCharge().toFixed(2)}
                </p>
              </li>
            )}
            <li className="remaining-amount">
              <span
                style={{
                  color: '#212121',
                  fontSize: '19px',
                  lineHeight: '19px',
                }}
              >
                Total After Tax
              </span>
              <p
                style={{
                  color: '#212121',
                  fontSize: '19px',
                  lineHeight: '19px',
                }}
              >
                ${calculateTotalAfterTax()}
              </p>
            </li>
          </ul>
        </div>
      )}
    </div>
  )
}

export default TableDetails
